'use client';

import { ReactNode, useEffect, useState } from 'react';

import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@kit/ui/carousel';

import { LandingIcons } from '../icons';
import GlowingBadge from '../ui/glowing-badge';
import SolutionCard from './solution-card';

export default function Component({
  id = 'solution',
  heading,
  subHeading,
  features,
}: {
  id?: string;
  heading?: string;
  subHeading?: string;
  features: {
    heading: string;
    image?: string;
    details: ReactNode;
  }[];
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const isDisablePrevious = current === 0;
  const isDisableNext = current === features.length - 1;

  const goToPrevious = () => {
    if (!isDisablePrevious) {
      if (current >= 1 && current <= features.length - 1) {
        setCurrent(current - 1);
      }
      api?.scrollTo(current - 1);
    }
  };

  const goToNext = () => {
    if (!isDisableNext) {
      if (current >= 1 && current <= features.length - 1) {
        setCurrent(current + 1);
      }
      api?.scrollTo(current + 1);
    }
  };

  return (
    <section id={id}>
      <div className="mx-auto my-2.5 flex max-w-1440 items-center justify-center px-2.5 lg:my-0 lg:px-4">
        <div className="max-w-full rounded-[20px] bg-[#141414] py-10 lg:rounded-[40px] lg:py-26">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="mx-auto grid h-full items-end gap-4 px-6 lg:grid-cols-2 lg:gap-13 lg:px-20"
          >
            <div>
              <div className="mb-4 flex lg:mb-3">
                <GlowingBadge>
                  <LandingIcons.features />
                  <span>Features</span>
                </GlowingBadge>
              </div>
              {heading ? (
                <h2 className="text-[32px] font-semibold leading-[1.2] text-[#e6e6e6] lg:text-5xl lg:leading-[1.4]">
                  {heading}
                </h2>
              ) : null}
            </div>
            {subHeading ? (
              <p className="text-sm leading-5 tracking-normal text-white/50 lg:text-lg lg:leading-7">
                {subHeading}
              </p>
            ) : null}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Carousel
              opts={{
                align: 'start',
              }}
              className="mt-10 w-full pl-6 lg:my-20 lg:pl-20"
              setApi={setApi}
            >
              <CarouselContent className="mr-16 flex">
                {features.map((feature, index) => (
                  <CarouselItem
                    key={index}
                    className="w-[234px] flex-none cursor-pointer lg:w-[416px]"
                  >
                    <SolutionCard
                      data={{
                        title: feature.heading,
                        image: feature.image,
                        content: feature.details,
                      }}
                      visibleBackground={current === index}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            <div className="mt-10 flex items-center justify-center lg:mt-0">
              <div className="flex gap-4">
                <Button
                  className="h-6 w-6 rounded-full bg-white/10 p-1 lg:h-12 lg:w-12 lg:px-4 lg:py-2"
                  onClick={goToPrevious}
                  disabled={isDisablePrevious}
                  aria-label="Previous"
                >
                  <ChevronLeft />
                </Button>
                <Button
                  className="h-6 w-6 rounded-full bg-white/10 p-1 lg:h-12 lg:w-12 lg:px-4 lg:py-2"
                  onClick={goToNext}
                  disabled={isDisableNext}
                  aria-label="Next"
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
